/**
 * 将十六进制颜色转换为RGB
 * @param {string} hex - 十六进制颜色代码
 * @returns {Array} - RGB数组 [r, g, b]
 */
const hexToRgb = (hex) => {
  // 移除 '#' 符号
  hex = hex.replace(/^#/, "");

  // 处理短格式颜色代码 '#03F' to '#0033FF'
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((x) => x + x)
      .join("");
  }

  const bigint = parseInt(hex, 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
};

/**
 * 将RGB颜色转换为十六进制颜色
 * @param {Array} rgb - RGB数组 [r, g, b]
 * @returns {string} - 十六进制颜色代码
 */
const rgbToHex = (rgb) => {
  return (
    "#" +
    rgb
      .map((x) => {
        const hex = x.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
};

/**
 * 获取渐变开始到结束中间的n个过渡颜色
 * @param {string} startColor - 起始颜色（十六进制）
 * @param {string} endColor - 结束颜色（十六进制）
 * @param {number} steps - 过渡颜色的数量
 * @returns {Array} - 包含所有过渡颜色的数组
 */
export const getGradientColors = (startColor, endColor, steps, isRgb) => {
  const startRgb = isRgb ? startColor : hexToRgb(startColor);
  const endRgb = isRgb ? endColor : hexToRgb(endColor);

  const stepFactor = 1 / (steps + 1);
  const interpolate = (start, end, factor) => start + (end - start) * factor;

  const gradientColors = [];
  for (let i = 1; i <= steps; i++) {
    const factor = stepFactor * i;
    const rgb = startRgb.map((start, index) =>
      Math.round(interpolate(start, endRgb[index], factor))
    );
    if (isRgb) {
      gradientColors.push(rgb);
    } else {
      gradientColors.push(rgbToHex(rgb));
    }
  }

  return gradientColors;
};

export const hslToRgb = ({ h, s, l }) => {
  s /= 100;  // 将饱和度转换为 0 到 1 之间
  l /= 100;  // 将亮度转换为 0 到 1 之间

  let c = (1 - Math.abs(2 * l - 1)) * s;
  let x = c * (1 - Math.abs((h / 60) % 2 - 1));
  let m = l - c / 2;

  let r, g, b;
  if (h >= 0 && h < 60) {
    r = c; g = x; b = 0;
  } else if (h >= 60 && h < 120) {
    r = x; g = c; b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0; g = c; b = x;
  } else if (h >= 180 && h < 240) {
    r = 0; g = x; b = c;
  } else if (h >= 240 && h < 300) {
    r = x; g = 0; b = c;
  } else {
    r = c; g = 0; b = x;
  }

  // 转换为 RGB 的标准范围（0-255）
  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);

  return { r, g, b };
};

export const getRandomHilightColor = (minS = 50, minl = 50, opacity = 0.2) => {
  const h = Math.floor(Math.random() * 360);
  const s = Math.floor(Math.random() * (100 - minS + 1)) + minS;
  const l = Math.floor(Math.random() * (100 - minl + 1)) + minl;
  const rgb = hslToRgb({h, s, l});
  return {
    color: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`,
    colorOpacity: `rgba(${rgb.r},${rgb.g},${rgb.b}, ${opacity})`
  }
}

export default {
  hexToRgb,
  rgbToHex,
  hslToRgb,
  getGradientColors,
  getRandomHilightColor

};
